import React from 'react';
import {
    fieldKeyFromBlock,
    formatCurrency,
    getValuePrice,
    getValueProperty,
    getValueThumbnail,
    getVisibleTextList,
    isFieldBlock,
    isTextListType,
    resolveCardLayout
} from '../../utils/valuePresentation';

const imageBoxStyle = {
    width: '100%',
    minHeight: '72px',
    height: '96px',
    objectFit: 'contain',
    borderRadius: '8px',
    background: 'var(--bg-secondary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-secondary)',
    fontSize: '0.7rem'
};

export function CardBlockContent({ blockId, value, schema, feature, showPlaceholders = false }) {
    if (blockId === 'title') {
        const title = value?.displayText || value?.value || (showPlaceholders ? 'Value title' : '');
        if (!title) return null;
        return <strong style={{ fontSize: '1rem' }}>{title}</strong>;
    }

    if (blockId === 'price') {
        const price = getValuePrice(value, schema, feature);
        if (price === '' && !showPlaceholders) return null;
        return (
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                {price === '' ? 'From price' : `From ${formatCurrency(price)}`}
            </span>
        );
    }

    if (blockId === 'description') {
        const boundDescription = feature?.dataBindings?.description
            ? getValueProperty(value, feature.dataBindings.description, '')
            : '';
        const description = boundDescription || value?.description || (showPlaceholders ? 'Description' : '');
        if (!description) return null;
        return <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{description}</span>;
    }

    if (!isFieldBlock(blockId)) return null;
    const key = fieldKeyFromBlock(blockId);
    const def = (schema || []).find(item => item.key === key);
    if (!def) return showPlaceholders ? <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Missing field</span> : null;

    if (isTextListType(def.type)) {
        const items = getVisibleTextList(getValueProperty(value, key, []));
        if (!items.length && !showPlaceholders) return null;
        const ListTag = def.type === 'numberedList' ? 'ol' : 'ul';
        return (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                <div>{def.label}</div>
                <ListTag style={{ margin: '0.15rem 0 0', paddingLeft: '1.1rem' }}>
                    {(items.length ? items : ['Item']).map((text, index) => <li key={index}>{text}</li>)}
                </ListTag>
            </div>
        );
    }

    const raw = getValueProperty(value, key, '');
    if ((raw === '' || raw === undefined || raw === null) && !showPlaceholders) return null;
    return (
        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            {def.label}: {raw === '' || raw === undefined || raw === null ? 'Value' : String(raw)}
        </span>
    );
}

export function ValueCardMedia({ value, schema, feature, showPlaceholders = false }) {
    const thumb = getValueThumbnail(value, schema, feature);
    if (thumb) {
        return <img src={thumb} alt="" style={imageBoxStyle} />;
    }
    if (!showPlaceholders) return null;
    return <div style={imageBoxStyle}>Image</div>;
}

export function ValueCardContent({
    value,
    schema = [],
    layout,
    feature,
    showPlaceholders = false,
    renderBlock,
    renderMedia
}) {
    const resolved = resolveCardLayout(layout, schema);
    const media = renderMedia
        ? renderMedia(resolved.imageRegion)
        : <ValueCardMedia value={value} schema={schema} feature={feature} showPlaceholders={showPlaceholders} />;
    const showMedia = resolved.imageRegion !== 'none' && (showPlaceholders || getValueThumbnail(value, schema, feature));
    const stack = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', minWidth: 0 }}>
            {resolved.blocks.map((block) => {
                const content = (
                    <CardBlockContent
                        key={block.id}
                        blockId={block.id}
                        value={value}
                        schema={schema}
                        feature={feature}
                        showPlaceholders={showPlaceholders}
                    />
                );
                if (renderBlock) return <React.Fragment key={block.id}>{renderBlock(block, content)}</React.Fragment>;
                return content ? <React.Fragment key={block.id}>{content}</React.Fragment> : null;
            })}
        </div>
    );

    if (!showMedia) {
        return stack;
    }

    if (resolved.imageRegion === 'top') {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {media}
                {stack}
            </div>
        );
    }

    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: resolved.imageRegion === 'left' ? '140px 1fr' : '1fr 140px',
            gap: '1rem',
            alignItems: 'start'
        }}>
            {resolved.imageRegion === 'left' ? media : stack}
            {resolved.imageRegion === 'left' ? stack : media}
        </div>
    );
}
